import { Pencil } from 'lucide-react';
import { CourseDetailsAdmin, CourseListItemUser } from '../../../../../../lib/interfaces/courses';
import { UserJob } from '../../../../../../lib/interfaces/user';
import { Button } from '../../../../../../ui/components/ui/button';
import CourseCard from '../../../../components/course-card/course-card';
import { USER_JOB_LABELS } from '../../../../constants/user-job-labels';
import ReleaseSwitch from '../../../components/release-switch/release-switch';

const AudienceBadges = ({ allJobs, userJobs }: { allJobs: boolean; userJobs: UserJob[] }) => {
  if (allJobs) {
    return (
      <span
        className="text-[11px] font-semibold px-2 py-1 rounded-full text-white"
        style={{ background: 'linear-gradient(135deg, #5FD5DE 0%, #C0DE60 100%)' }}>
        Alle Rollen
      </span>
    );
  }
  if (userJobs.length === 0) {
    return <span className="text-[11px] text-[#888] italic">Keine Zielgruppe</span>;
  }
  return (
    <div className="flex gap-1 flex-wrap">
      {userJobs.map((job) => (
        <span key={job} className="text-[11px] font-medium px-2 py-1 rounded-full bg-[#F0F4FC] text-[#12282A]">
          {USER_JOB_LABELS[job]}
        </span>
      ))}
    </div>
  );
};

export interface CourseHeaderProps {
  course: CourseDetailsAdmin;
  onEditMeta: () => void;
  onToggleRelease: (nextIsReleased: boolean) => void;
  isTogglingRelease?: boolean;
}

const SettingsRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-[#E4E7EC] last:border-b-0">
    <span className="text-[13px] text-[#888]">{label}</span>
    {children}
  </div>
);

const CourseHeader = ({ course, onEditMeta, onToggleRelease, isTogglingRelease }: CourseHeaderProps) => {
  const lessonCount = course.modules.reduce((total, module) => total + module.lessons.length, 0);
  const canRelease = lessonCount > 0;

  // The preview renders the real user card, so cropping and title truncation are shown exactly as
  // users will see them — a rebuilt lookalike would drift the moment the card changes.
  const previewCourse: CourseListItemUser = {
    _id: course._id,
    title: course.title,
    imageUrl: course.imageUrl,
    createdAt: course.createdAt,
    percentage: course.isReleased ? 100 : 0
  };

  return (
    // Same grid as the user course list — that makes the preview column exactly one card wide.
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
      <div className="flex flex-col gap-2">
        <CourseCard course={previewCourse} percentage={previewCourse.percentage} onOpen={() => undefined} isPreview={true} />
      </div>

      <div className="flex flex-col gap-2">
        <div
          className="bg-white rounded-2xl border border-[#E4E7EC] px-5 py-2 flex-1 flex flex-col justify-center"
          style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.04)' }}>
          <SettingsRow label="Sichtbarkeit">
            <ReleaseSwitch isReleased={course.isReleased} onChange={onToggleRelease} canRelease={canRelease} isBusy={isTogglingRelease} />
          </SettingsRow>
          <SettingsRow label="Zielgruppe">
            <AudienceBadges allJobs={course.audience.allJobs} userJobs={course.audience.userJobs} />
          </SettingsRow>
          <SettingsRow label="Inhalt">
            <div className="flex items-center gap-3">
              <span className="text-[13px] text-[#12282A]">
                {course.modules.length} {course.modules.length === 1 ? 'Modul' : 'Module'} · {lessonCount}{' '}
                {lessonCount === 1 ? 'Lektion' : 'Lektionen'}
              </span>
              <Button variant="outline" size="sm" onClick={onEditMeta}>
                <Pencil className="w-3.5 h-3.5 mr-1.5" />
                Bearbeiten
              </Button>
            </div>
          </SettingsRow>
        </div>
      </div>
    </div>
  );
};

export default CourseHeader;
